import { FC, useState } from 'react'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
// others
import { Nullable } from '../../../interfaceTypes/common'
import { PayableStatus, PaymentMethod } from '../../../../generated'
import { PayableRowActionsProps } from '../../../interfaceTypes/props'
import { anchorOrigin, transformOrigin } from '../../../theme/styleConstants'

/**
 * One kebab menu per row, replacing the row of bare icons. The cheque actions could not be added as
 * icons — the column was already at its width, and no icon says "this draws a real check on real
 * stock" clearly enough for an action that marks the voucher Paid.
 */
export const PayableRowActions: FC<PayableRowActionsProps> = ({
  payable,
  isPreviewing,
  onView,
  onEdit,
  onDelete,
  onVoid,
  onVoidAndDuplicate,
  onPreviewCheck,
  onPrintCheck
}) => {
  const { status, paymentMethod, printedAt } = payable || {}
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null)
  const closeMenu = () => setAnchorEl(null)

  // Every item closes the menu before acting: the ones that navigate away or open a modal would
  // otherwise leave it floating over the new view.
  const run = (action: () => void) => () => {
    closeMenu()
    action()
  }

  const isVoided = status === PayableStatus.Voided
  // A voided cheque must never be drawn again, and a voucher paid any other way has no leaf to print
  // on — the server's plan rejects both, so the options are not offered in the first place.
  const isPrintableCheck = paymentMethod === PaymentMethod.Cheque && !isVoided

  return (
    <>
      <Tooltip title="Actions">
        <IconButton size="small" onClick={event => setAnchorEl(event.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          paper: {
            sx: { minWidth: '180px' }
          }
        }}
      >
        <MenuItem onClick={run(onView)}>View Details</MenuItem>

        <MenuItem onClick={run(onEdit)} disabled={isVoided}>
          Edit
        </MenuItem>

        {isPrintableCheck && (
          <>
            <MenuItem onClick={run(onPrintCheck)}>{printedAt ? 'Reprint Check' : 'Print Check'}</MenuItem>
            <MenuItem onClick={run(onPreviewCheck)} disabled={isPreviewing}>
              Preview Check
            </MenuItem>
          </>
        )}

        {status === PayableStatus.Unpaid && <MenuItem onClick={run(onDelete)}>Delete</MenuItem>}

        {status === PayableStatus.Paid && (
          <>
            <MenuItem onClick={run(onVoid)}>Void</MenuItem>
            <MenuItem onClick={run(onVoidAndDuplicate)}>Void &amp; Duplicate</MenuItem>
          </>
        )}
      </Menu>
    </>
  )
}
